import { useState } from "react";
import { api } from "../lib/api";
import { useFetch } from "../lib/useFetch";

interface WorkerRow { id: number; name: string; role: string; status: ComplianceStatus }
interface Counts { compliant: number; expiring: number; expired: number; incomplete: number }
interface Summary { workers: WorkerRow[]; counts: Counts }
interface ComplianceRecord {
  itemId: number; name: string; mandatory: boolean;
  reference: string | null; expiryDate: string | null; status: ItemStatus;
}
interface Detail { worker: { id: number; name: string }; status: ComplianceStatus; records: ComplianceRecord[]; missingMandatory: string[] }

type ComplianceStatus = "compliant" | "expiring" | "expired" | "incomplete";
type ItemStatus = "valid" | "expiring" | "expired" | "missing";

const statusChip: Record<string, string> = {
  compliant: "bg-teal-50 text-teal-700", valid: "bg-teal-50 text-teal-700",
  expiring: "bg-amber-50 text-amber-700",
  expired: "bg-red-100 text-red-700",
  incomplete: "bg-slate-100 text-body", missing: "bg-slate-100 text-faint",
};
const statusLabel: Record<string, string> = {
  compliant: "Compliant", expiring: "Expiring soon", expired: "Expired",
  incomplete: "Incomplete", valid: "Valid", missing: "Not on file",
};

export default function Compliance() {
  const summary = useFetch<Summary>("/v1/workforce/compliance");
  const [selected, setSelected] = useState<number | null>(null);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-ink">Workforce compliance</h1>
        <p className="text-sm text-faint mt-1">
          Expired mandatory documents block new rostering (FR-WFC-02).
        </p>
      </div>

      {summary.loading && <div className="skeleton h-40" />}
      {summary.error && <p className="text-sm text-red-600">{summary.error}</p>}
      {summary.data && (
        <>
          <CountsBar counts={summary.data.counts} />
          <div className="card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-edge-soft">
                  <th className="th">Worker</th><th className="th">Role</th>
                  <th className="th">Status</th><th className="th"></th>
                </tr>
              </thead>
              <tbody>
                {summary.data.workers.map((w) => (
                  <tr key={w.id} className="border-b border-edge-soft last:border-0">
                    <td className="px-5 py-3 font-medium text-ink">{w.name}</td>
                    <td className="px-5 py-3 text-mut capitalize">{w.role}</td>
                    <td className="px-5 py-3">
                      <span className={`chip ${statusChip[w.status]}`}>{statusLabel[w.status]}</span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button className="text-sm text-teal-700 hover:underline"
                        onClick={() => setSelected(w.id === selected ? null : w.id)}>
                        {w.id === selected ? "Close" : "Manage"}
                      </button>
                    </td>
                  </tr>
                ))}
                {summary.data.workers.length === 0 && (
                  <tr><td className="px-5 py-6 text-mut" colSpan={4}>No support workers or coordinators yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {selected !== null && (
            <WorkerCompliance workerId={selected} onSaved={summary.reload} />
          )}
        </>
      )}
    </div>
  );
}

function CountsBar({ counts }: { counts: Counts }) {
  const cells: Array<[string, number, ComplianceStatus]> = [
    ["Compliant", counts.compliant, "compliant"],
    ["Expiring soon", counts.expiring, "expiring"],
    ["Expired", counts.expired, "expired"],
    ["Incomplete", counts.incomplete, "incomplete"],
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {cells.map(([label, n, s]) => (
        <div key={label} className="card p-4">
          <p className="text-2xl font-semibold text-ink">{n}</p>
          <span className={`chip mt-1 ${statusChip[s]}`}>{label}</span>
        </div>
      ))}
    </div>
  );
}

function WorkerCompliance({ workerId, onSaved }: { workerId: number; onSaved: () => void }) {
  const detail = useFetch<Detail>(`/v1/workforce/workers/${workerId}/compliance`);

  async function save(itemId: number, expiryDate: string, reference: string) {
    await api(`/v1/workforce/workers/${workerId}/compliance`, {
      method: "POST",
      body: JSON.stringify({ itemId, expiryDate: expiryDate || undefined, reference: reference || undefined }),
    });
    detail.reload();
    onSaved();
  }

  if (detail.loading) return <div className="skeleton h-40" />;
  if (detail.error) return <p className="text-sm text-red-600">{detail.error}</p>;
  if (!detail.data) return null;

  return (
    <div className="card p-5 space-y-4">
      <h2 className="text-lg font-semibold text-ink">{detail.data.worker.name} — documents</h2>
      <div className="space-y-2">
        {detail.data.records.map((r) => (
          <RecordRow key={r.itemId} record={r} onSave={save} />
        ))}
      </div>
    </div>
  );
}

function RecordRow({ record, onSave }: { record: ComplianceRecord; onSave: (itemId: number, expiry: string, ref: string) => Promise<void> }) {
  const [expiry, setExpiry] = useState(record.expiryDate ?? "");
  const [reference, setReference] = useState(record.reference ?? "");
  const [saving, setSaving] = useState(false);

  async function submit() {
    setSaving(true);
    try { await onSave(record.itemId, expiry, reference); }
    finally { setSaving(false); }
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-2 items-center border-b border-edge-soft pb-2 last:border-0">
      <div>
        <span className="text-sm font-medium text-ink">{record.name}</span>
        {record.mandatory && <span className="ml-2 text-xs text-faint">mandatory</span>}
        <span className={`chip ml-2 ${statusChip[record.status]}`}>{statusLabel[record.status]}</span>
      </div>
      <input value={reference} onChange={(e) => setReference(e.target.value)}
        placeholder="Reference #" className="input mt-0 w-36 text-sm" />
      <input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)}
        className="input mt-0 w-40 text-sm" />
      <button onClick={submit} disabled={saving} className="btn-dark text-sm disabled:opacity-50">
        {saving ? "Saving…" : "Save"}
      </button>
    </div>
  );
}
